import { z } from 'zod';
import { JUSTIFICATION_REASONS } from './attendance';
import { AnnouncementSchema, TimetableEntrySchema } from './timetable';

/** Student app home (MobStudent « Accueil »). */
export const StudentHomeSchema = z.object({
  studentId: z.uuid(),
  firstName: z.string(),
  matricule: z.string(),
  yearLabel: z.string().nullable(),
  levelCode: z.string().nullable(),
  groupName: z.string().nullable(),
  next: TimetableEntrySchema.nullable(),
  average: z
    .object({ semester: z.number().int(), value: z.number().nullable(), decision: z.string().nullable() })
    .nullable(),
  balance: z
    .object({
      remainingMillimes: z.number().int(),
      next: z.object({ label: z.string(), dueOn: z.string() }).nullable(),
    })
    .nullable(),
  absences: z.object({ hours: z.number(), toJustifyHours: z.number() }),
  documents: z.number().int(),
  announcements: z.array(AnnouncementSchema),
  unreadAnnouncements: z.number().int(),
});
export type StudentHome = z.infer<typeof StudentHomeSchema>;

export const StudentDaySchema = z.object({
  monday: z.string(),
  days: z.array(z.object({ date: z.string(), weekday: z.number().int() })),
  entries: z.array(TimetableEntrySchema),
});
export type StudentWeek = z.infer<typeof StudentDaySchema>;

/** Justification sent by the student (MobStudent « Envoyer un justificatif »); checked by the registrar. */
export const StudentJustificationSchema = z.object({
  reason: z.enum(JUSTIFICATION_REASONS),
  note: z.string().trim().max(300).optional(),
  recordIds: z.array(z.uuid()).min(1).max(50),
});
export type StudentJustification = z.infer<typeof StudentJustificationSchema>;

export const JustificationDecisionSchema = z.object({
  approve: z.boolean(),
  reason: z.string().trim().max(300).optional(),
});

export const PendingJustificationSchema = z.object({
  id: z.uuid(),
  studentId: z.uuid(),
  studentName: z.string(),
  matricule: z.string(),
  reason: z.enum(JUSTIFICATION_REASONS),
  note: z.string().nullable(),
  fileName: z.string().nullable(),
  hours: z.number(),
  courses: z.array(z.string()),
  createdAt: z.string(),
});
export type PendingJustification = z.infer<typeof PendingJustificationSchema>;

export const DOCUMENT_REQUEST_KINDS = [
  'enrollment_certificate',
  'attendance_certificate',
  'transcript',
  'internship',
  'other',
] as const;
export type DocumentRequestKind = (typeof DOCUMENT_REQUEST_KINDS)[number];
export const DocumentRequestInputSchema = z.object({
  kind: z.enum(DOCUMENT_REQUEST_KINDS),
  note: z.string().trim().max(500).optional(),
});
export const DocumentRequestSchema = z.object({
  id: z.uuid(),
  kind: z.enum(DOCUMENT_REQUEST_KINDS),
  note: z.string().nullable(),
  status: z.enum(['pending', 'done', 'rejected']),
  studentId: z.uuid(),
  studentName: z.string(),
  matricule: z.string(),
  answer: z.string().nullable(),
  createdAt: z.string(),
  handledAt: z.string().nullable(),
});
export type DocumentRequest = z.infer<typeof DocumentRequestSchema>;
export const DocumentRequestDecisionSchema = z.object({
  status: z.enum(['done', 'rejected']),
  answer: z.string().trim().max(500).optional(),
});
